import { Builder } from '@builder.io/react';
import classNames from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import { useTranslation } from 'next-i18next/pages';
import { FC } from 'react';

import {
  LotCard,
  Scrollable,
  I18nInstance,
  ILotCard,
} from '@catawiki/cata-style';

import { EnrichedLot, enrichLotsWithPriceData, Lot } from '@/shared/models/lot';
import { getBuilderIOComponentClassName } from '@/shared/utils/getBuilderIOComponentClassName';
import { nullableData } from '@/shared/utils/http';

import { fetchLotPrices, fetchLots } from '../../api/buyer';
import {
  BACKGROUND_COLOR_NAMES,
  BackgroundClassMap,
  COLOR_NAME_WHITE,
} from '../../constants/colors';
import { MockLot } from '../../stubs/lot';
import { cleanBuilderStringInput } from '../../text';
import { Title } from './Title';

interface LotIdInput {
  lotId: number;
}

interface BuilderInput {
  title?: string;
  label?: string;
  backgroundColorClassName: string;
  lotIds?: LotIdInput[];
  lotsUrl?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: any;
}

type Props = BuilderInput & {
  locale: string;
  lots?: EnrichedLot[];
};

function convertToLotCardLot(lot: EnrichedLot, statusText: string): ILotCard {
  return {
    title: lot.title,
    imageUrl: lot.imageUrl,
    lotUrl: lot.lotUrl,
    status: undefined,
    statusText,
  };
}

function ExploreMoreLink({ link }: { link?: string }) {
  const { t } = useTranslation();
  if (!link) return null;
  return (
    <a
      className="u-typography-h6 tw:text-brand-primary"
      href={cleanBuilderStringInput(link, true)}
    >
      {t('bid_status_section.explore_more')}
    </a>
  );
}

function Label({
  label,
  exploreMoreUrl,
}: {
  label?: string;
  exploreMoreUrl?: string;
}) {
  if (!label && !exploreMoreUrl) return null;
  return (
    <div className="tw:flex tw:justify-between u-m-b-xl">
      {label && (
        <h6 className="u-typography-h6">
          {cleanBuilderStringInput(label, true)}
        </h6>
      )}
      <ExploreMoreLink link={exploreMoreUrl} />
    </div>
  );
}

export const Lots: FC<Props> = ({
  backgroundColorClassName,
  title,
  locale,
  label,
  lotsUrl,
  lotIds = [],
  lots = [],
  attributes,
}) => {
  const { t } = useTranslation();
  const styles = getBuilderIOComponentClassName(attributes);
  const spacing =
    styles || classNames('u-p-v-xxl-6', 'u-p-v-sm-xxl-4', 'u-p-v-md-xxl-4');

  let componentLots = lots;
  if (lotIds.length === 0) {
    componentLots = [MockLot({ locale, id: 1 })];
  }

  if (lotIds.length > 0 && lots.length === 0) {
    componentLots = lotIds.map((item) => MockLot({ locale, id: item.lotId }));
  }

  return (
    <div {...attributes} className={classNames(backgroundColorClassName)}>
      <div className={classNames('u-wrapper', spacing)}>
        <Title title={title} className={classNames('u-m-b-xl')} />
        <div className={classNames('p-lots')}>
          <Label label={label} exploreMoreUrl={lotsUrl} />
          <Scrollable listClassName="tw:items-center u-g-m">
            {componentLots.map((lot) => {
              const lotStatusText = lot.closed
                ? t('similar_lots.sold_for')
                : t('short_auction_current_bid');
              return (
                <LotCard
                  lot={convertToLotCardLot(lot, lotStatusText)}
                  price={lot.price}
                  key={lot.id}
                />
              );
            })}
          </Scrollable>
        </div>
      </div>
    </div>
  );
};

async function fetchLotItems(
  i18n: I18nInstance,
  locale: string,
  lotIds: { lotId: number }[]
) {
  if (lotIds.length === 0) return [];
  const ids = lotIds.map((item) => item.lotId);

  const [eitherLots, eitherLotPrices] = await Promise.all([
    fetchLots(locale, ids),
    fetchLotPrices(ids),
  ]);

  const remoteLots = pipe(eitherLots, nullableData) || [];
  const lotPrices = pipe(eitherLotPrices, nullableData) || [];

  const lotsMap = remoteLots.reduce(
    (out, lot) => ({
      ...out,
      [lot.id]: {
        id: lot.id,
        imageUrl: lot.original_image_url,
        lotUrl: lot.url,
        title: lot.title,
      },
    }),

    {} as { [key in number]: Lot }
  );

  const lots = ids.map((id) => lotsMap[id]);
  const lotsWithPriceData = enrichLotsWithPriceData(i18n, lots, lotPrices);

  return lotsWithPriceData;
}

export function dataFetcher(locale: string, i18n: I18nInstance) {
  return async ({ lotIds = [] }: BuilderInput) => {
    const lots = await fetchLotItems(i18n, locale, lotIds);
    return {
      locale,
      lots,
    };
  };
}

export function componentRegister() {
  const name = 'Lots';
  Builder.registerComponent(Lots, {
    name,
    image: `https://${process.env.NEXT_PUBLIC_BASE_DOMAIN}/assets/pages/images/icons/lots.svg`,
    inputs: [
      { name: 'title', type: 'string', defaultValue: 'Title' },
      {
        name: 'label',
        friendlyName: 'Label over lot',
        defaultValue: 'Label',
        type: 'string',
      },
      {
        name: 'backgroundColorClassName',
        friendlyName: 'Background Color',
        type: 'string',
        defaultValue: BackgroundClassMap[COLOR_NAME_WHITE],
        enum: BACKGROUND_COLOR_NAMES.map((value) => ({
          label: value,
          value: BackgroundClassMap[value],
        })),
      },
      {
        name: 'lotIds',
        type: 'list',
        friendlyName: 'Lot list',
        subFields: [{ name: 'lotId', friendlyName: 'Lot ID', type: 'number' }],
      },
      {
        name: 'lotsUrl',
        type: 'string',
        friendlyName: 'URL to lot page',
      },
    ],
    hideFromInsertMenu: true,
    noWrap: true,
  });
  return name;
}
